/**
 * 章节编辑器组件
 *
 * 支持章节正文编辑,包括:
 * - Markdown编辑
 * - 格式化工具栏(字体大小、行间距)
 * - 文字锁定功能
 * - 编辑历史查看
 * - 章节摘要显示和生成
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { Chapter } from '../../types/chapter';
import { ChapterService } from '../../services/chapter';
import { DiffViewer } from './DiffViewer';
import { Icons } from './EditorIcons';
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages';
import { useAutoTrackStore } from '../../store/autoTrackStore';
import { useTextSelectionStore } from '../../store/textSelectionStore';
import { KnowledgeGraphModal } from '../knowledge/KnowledgeGraphModal';

// ==================== 工具函数 ====================

/**
 * 获取 textarea 中指定字符位置的精确坐标
 * 使用 mirror div 技术实现像素级精确
 */
const getCaretCoordinates = (textarea: HTMLTextAreaElement, position: number): { top: number; left: number; height: number } => {
  // 创建 mirror div
  const mirror = document.createElement('div');
  mirror.id = 'textarea-caret-position-mirror-div';
  mirror.style.cssText = `
    position: absolute;
    visibility: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow: hidden;
    z-index: -1000;
  `;

  // 复制 textarea 的所有相关样式
  const style = getComputedStyle(textarea);
  const properties = [
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
    'letterSpacing', 'textTransform', 'wordSpacing',
    'textIndent', 'whiteSpace', 'wordBreak', 'wordWrap',
    'lineHeight', 'padding', 'paddingTop', 'paddingRight',
    'paddingBottom', 'paddingLeft', 'border', 'borderWidth',
    'boxSizing', 'width'
  ];

  properties.forEach(prop => {
    mirror.style[prop as any] = style.getPropertyValue(prop.replace(/([A-Z])/g, '-$1').toLowerCase());
  });

  // 设置宽度
  mirror.style.width = `${textarea.clientWidth}px`;

  // 设置内容到指定位置
  const text = textarea.value.substring(0, position);

  // 处理文本中的特殊字符
  const span = document.createElement('span');
  span.style.whiteSpace = 'pre-wrap';
  span.textContent = text;
  mirror.appendChild(span);

  // 创建标记元素获取位置
  const marker = document.createElement('span');
  marker.textContent = '|';
  mirror.appendChild(marker);

  document.body.appendChild(mirror);

  const coordinates = {
    top: marker.offsetTop,
    left: marker.offsetLeft,
    height: parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6
  };

  document.body.removeChild(mirror);

  return coordinates;
};

// ==================== 常量定义 ====================

const FONT_SIZES = [12, 14, 16, 18, 20, 22, 24];
const LINE_HEIGHTS = [1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.5];
const DEBOUNCE_DELAY = 1000; // 防抖延迟1秒
const MIN_CONTENT_LENGTH_FOR_ABSTRACT = 1500; // 摘要生成的最小内容长度

// ==================== 组件Props接口 ====================

interface ChapterEditorProps {
  /** 章节对象 */
  chapter: Chapter;
  /** 内容变更回调 */
  onContentChange?: (content: string) => void;
  /** 章节更新回调(自动/手动保存成功后) */
  onChapterUpdate?: (updatedChapter: Chapter) => void;
  /** 保存回调(手动保存) */
  onSave?: () => Promise<void>;
  /** 显示锁定按钮 */
  showLockButton?: boolean;
  /** 显示历史按钮 */
  showHistoryButton?: boolean;
  /** 是否只读 */
  readOnly?: boolean;
  /** ChapterService实例(可选) */
  chapterService?: ChapterService;
}

// ==================== 主组件 ====================

export const ChapterEditor: React.FC<ChapterEditorProps> = ({
  chapter,
  onContentChange,
  onChapterUpdate,
  onSave,
  showLockButton = true,
  showHistoryButton = true,
  readOnly = false,
  chapterService: externalChapterService
}) => {
  // ==================== 状态管理 ====================

  const [content, setContent] = useState<string>(chapter.content || '');
  const [fontSize, setFontSize] = useState<number>(16);
  const [lineHeight, setLineHeight] = useState<number>(1.6);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMountedRef = useRef<boolean>(true);

  // 锁定功能相关状态
  const [isLocking, setIsLocking] = useState<boolean>(false);

  // 历史功能相关状态
  const [showHistory, setShowHistory] = useState<boolean>(false);

  // 错误提示
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  // 摘要相关状态
  const [isGeneratingAbstract, setIsGeneratingAbstract] = useState<boolean>(false);
  const [abstractCleared, setAbstractCleared] = useState<boolean>(false);

  // 知识图谱模态框状态
  const [showGraphModal, setShowGraphModal] = useState<boolean>(false);

  // 高亮效果状态
  const [showHighlight, setShowHighlight] = useState<boolean>(false);
  const [updateNotice, setUpdateNotice] = useState<{
    oldText?: string;
    newText?: string;
    startOffset?: number;
    endOffset?: number;
  } | null>(null);
  const highlightInfo = useAutoTrackStore((state) => state.highlightInfo);
  const clearHighlight = useAutoTrackStore((state) => state.clearHighlight);

  // 文字选中弹窗状态
  const showPopup = useTextSelectionStore((state) => state.showPopup);
  const hidePopup = useTextSelectionStore((state) => state.hidePopup);

  // ==================== 公共章节锁定状态 ====================

  // 公共章节（分支点之前的章节）正文锁定，标题可编辑
  const isContentLocked = chapter.is_common || chapter.is_locked || false;
  // 整体只读状态：用户设置的只读 或 内容被锁定
  const isEditorReadOnly = readOnly || isContentLocked;

  // ==================== 服务实例 ====================

  const [chapterService] = useState<ChapterService>(
    () => externalChapterService || new ChapterService(undefined)
  );

  // ==================== 防抖保存 ====================

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 自动保存函数
  const autoSave = useCallback(async (newContent: string) => {
    // 公共章节不自动保存内容
    if (isContentLocked) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
try {
        setIsSaving(true);
        setSaveStatus('idle');

        const updatedChapter = await chapterService.updateChapter(chapter.workspace_id, chapter.id, {
          content: newContent
        });

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);

        // 通知父组件更新章节数据
        onChapterUpdate?.(updatedChapter);
      } catch (err) {
        console.error('[ChapterEditor] 保存失败:', err);
        setSaveStatus('error');
        setErrorMessage(getUserFriendlyErrorMessage(err));
      } finally {
        setIsSaving(false);
      }
    }, DEBOUNCE_DELAY);
  }, [chapter.workspace_id, chapter.id, chapterService, onChapterUpdate, isContentLocked]);

  // ==================== 摘要生成 ====================
  const handleGenerateAbstract = useCallback(async () => {
    // 检查章节是否有内容
    if (!content || !content.trim()) {
      setErrorMessage('章节内容为空，无法生成摘要');
      return;
    }

    // 检查内容长度
    if (content.length < MIN_CONTENT_LENGTH_FOR_ABSTRACT) {
      setErrorMessage(`章节内容不足，至少需要${MIN_CONTENT_LENGTH_FOR_ABSTRACT}字才能生成摘要`);
      return;
    }

    try {
      setIsGeneratingAbstract(true);
      setErrorMessage(null);

      // 调用摘要生成API
      const updatedChapter = await chapterService.generateAbstract(
        chapter.workspace_id,
        chapter.id,
        abstractCleared || !!chapter.chapter_abstract // force_regenerate
      );

      // 更新章节信息
      onChapterUpdate?.(updatedChapter);
      setAbstractCleared(false);

    } catch (err) {
      console.error('[ChapterEditor] 生成摘要失败:', err);
      setErrorMessage(getUserFriendlyErrorMessage(err));
    } finally {
      setIsGeneratingAbstract(false);
    }
  }, [chapter.workspace_id, chapter.id, chapter.chapter_abstract, content, abstractCleared, chapterService, onChapterUpdate]);


  // ==================== 内容变更处理 ====================

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
    onContentChange?.(newContent);

    // 检测内容是否发生了实质性变化（如果有摘要的话）
    if (chapter.chapter_abstract && !abstractCleared) {
      const contentChanged = newContent !== chapter.content;
      if (contentChanged) {
        setAbstractCleared(true);
      }
    }

    // 如果不是只读模式,触发自动保存
    if (!isEditorReadOnly) {
      autoSave(newContent);
    }
  }, [onContentChange, isEditorReadOnly, autoSave, chapter.chapter_abstract, chapter.content, abstractCleared]);

  // ==================== 字体大小调整 ====================

  const handleFontSizeChange = useCallback((size: number) => {
    setFontSize(size);
  }, []);

  // ==================== 行间距调整 ====================

  const handleLineHeightChange = useCallback((height: number) => {
    setLineHeight(height);
  }, []);

  // ==================== 文字选择处理 ====================

  const handleSelectionChange = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // 文字选择变化时的处理（当前未使用，保留用于未来扩展）
    if (start !== end) {
      console.log('[ChapterEditor] 文字已选中:', { start, end });
    }
  }, []);

  // 鼠标松开时处理文字选中，显示快捷菜单
  const handleMouseUp = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // 只有选中文字时才显示弹窗
    if (start !== end) {
      const selectedText = textarea.value.substring(start, end);

      // 只在有实际选中内容时显示
      if (selectedText.trim()) {
        // 使用现有的 getCaretCoordinates 函数获取精确位置
        const coords = getCaretCoordinates(textarea, end);
        const rect = textarea.getBoundingClientRect();

        // 计算弹窗位置（相对于视口）
        // 需要减去 textarea 的 scrollTop，因为 getCaretCoordinates 返回的是相对于内容顶部的位置
        const adjustedTop = coords.top - textarea.scrollTop;

        showPopup({
          text: selectedText,
          position: {
            x: rect.left + coords.left,
            y: rect.top + adjustedTop + coords.height
          },
          sourceType: 'chapter',
          sourceId: chapter.id,
          contextInfo: {
            chapterTitle: chapter.name
          }
        });
      }
    } else {
      // 没有选中文字时隐藏弹窗
      hidePopup();
    }
  }, [chapter.id, chapter.name, showPopup, hidePopup]);

  // ==================== 锁定章节 ====================

  const handleLockToggle = useCallback(async () => {
    if (isLocking) return;

    try {
      setIsLocking(true);
      setErrorMessage(null);

      const action = chapter.is_locked ? 'unlock' : 'lock';
      const updatedChapter = await chapterService.lockChapter(
        chapter.workspace_id,
        chapter.id,
        action
      );

      // 通知父组件章节已更新
      if (isMountedRef.current && onChapterUpdate) {
        onChapterUpdate(updatedChapter);
      }
    } catch (err) {
      console.error('[ChapterEditor] 锁定操作失败:', err);
      if (isMountedRef.current) {
        setErrorMessage(chapter.is_locked ? '解锁失败,请重试' : '锁定失败,请重试');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLocking(false);
      }
    }
  }, [chapter, isLocking, chapterService, onChapterUpdate]);

  // ==================== 编辑历史 ====================

  const handleShowHistory = useCallback(() => {
    setShowHistory(!showHistory);
  }, [showHistory]);

  // ==================== 手动保存 ====================

  const handleManualSave = useCallback(async () => {
    if (isSaving || readOnly) return;

    try {
      setIsSaving(true);
      setSaveStatus('idle');

      const updatedChapter = await chapterService.updateChapter(chapter.workspace_id, chapter.id, {
        content
      });

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);

      // 通知父组件更新章节数据
      onChapterUpdate?.(updatedChapter);

      onSave?.();
    } catch (err) {
      console.error('[ChapterEditor] 保存失败:', err);
      setSaveStatus('error');
      setErrorMessage(getUserFriendlyErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  }, [chapter.workspace_id, chapter.id, content, isSaving, readOnly, chapterService, onChapterUpdate, onSave]);

  // ==================== 效果钩子 ====================

  // 当chapter变化时,更新content（监听 chapter.id 以检测章节切换）
  useEffect(() => {
    setContent(chapter.content || '');
    // 切换章节时重置摘要清除标志
    setAbstractCleared(false);
  }, [chapter.id, chapter.content]);



  // 组件卸载时清理定时器,防止内存泄漏
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // 监听高亮效果
  useEffect(() => {
    // 检查是否有高亮信息且匹配当前章节
    if (highlightInfo &&
        highlightInfo.targetType === 'chapter' &&
        highlightInfo.targetId === chapter.id) {

      // 判断是否是 update 操作（有位置信息）
      if (highlightInfo.startOffset !== undefined && highlightInfo.startOffset >= 0) {
        // chapter_update: 滚动到修改位置并显示提示
        const textarea = textareaRef.current;
        if (textarea) {
          // 使用精确坐标计算滚动位置
          requestAnimationFrame(() => {
            const coords = getCaretCoordinates(textarea, highlightInfo.startOffset!);

            // 计算目标滚动位置，使目标显示在视口上 1/3 处
            const targetScrollTop = Math.max(0, coords.top - textarea.clientHeight / 3);
            textarea.scrollTop = targetScrollTop;

            // 设置光标位置到修改处
            textarea.focus();
            textarea.setSelectionRange(
              highlightInfo.startOffset!,
              highlightInfo.endOffset ?? highlightInfo.startOffset!
            );
          });
        }

        // 显示修改提示（从 highlightInfo 获取或使用默认值）
        setUpdateNotice({
          startOffset: highlightInfo.startOffset,
          endOffset: highlightInfo.endOffset,
        });

        // 定时清除
        const timer = setTimeout(() => {
          setUpdateNotice(null);
          clearHighlight();
        }, highlightInfo.duration);

        return () => clearTimeout(timer);
      } else {
        // chapter_write: 全编辑器高亮
        setShowHighlight(true);

        // 滚动到顶部
        if (textareaRef.current) {
          textareaRef.current.scrollTop = 0;
        }

        const timer = setTimeout(() => {
          setShowHighlight(false);
          clearHighlight();
        }, highlightInfo.duration);

        return () => clearTimeout(timer);
      }
    }
  }, [highlightInfo, chapter.id, clearHighlight]);

  // ==================== 渲染 ====================

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 工具栏 */}
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 overflow-x-auto">
          {/* 字体大小 */}
          <select
            value={fontSize}
            onChange={(e) => handleFontSizeChange(Number(e.target.value))}
            disabled={isEditorReadOnly}
            className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 disabled:opacity-50 transition-all"
          >
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>

          {/* 行间距 */}
          <select
            value={lineHeight}
            onChange={(e) => handleLineHeightChange(Number(e.target.value))}
            disabled={isEditorReadOnly}
            className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 disabled:opacity-50 transition-all"
          >
            {LINE_HEIGHTS.map((height) => (
              <option key={height} value={height}>
                {height.toFixed(1)}
              </option>
            ))}
          </select>

          <div className="w-px h-6 bg-gray-200" />

          {/* 锁定章节 */}
          {showLockButton && (
            <button
              onClick={handleLockToggle}
              disabled={isLocking}
              className={`p-2 rounded-lg transition-colors flex items-center gap-1 ${
                chapter.is_locked
                  ? 'text-amber-600 hover:bg-amber-50'
                  : 'text-gray-600 hover:bg-gray-100'
              } disabled:opacity-50 disabled:hover:bg-transparent`}
              title={chapter.is_locked ? '解锁章节（AI将无法修改）' : '锁定章节（AI将无法修改）'}
            >
              {isLocking ? Icons.loading : chapter.is_locked ? Icons.lock({}) : Icons.unlock({})}
            </button>
          )}

          {/* 编辑历史 */}
          {showHistoryButton && (
            <button
              onClick={handleShowHistory}
              disabled={readOnly}
              className={`p-2 rounded transition-colors ${
                showHistory
                  ? 'text-spine-500 bg-gray-100'
                  : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
              } disabled:opacity-50 disabled:hover:bg-transparent`}
              title="查看编辑历史"
            >
              <Icons.history />
            </button>
          )}
        </div>

        {/* 保存按钮和状态 */}
        <div className="flex items-center gap-3">
          {/* 保存状态指示器 */}
          <div className="flex items-center gap-1 min-w-[60px]">
            {saveStatus === 'saved' && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <Icons.check />
                已保存
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="text-sm text-red-600">保存失败</span>
            )}
            {isSaving && (
              <span className="text-sm text-gray-600 flex items-center gap-1">
                {Icons.loading}
                保存中...
              </span>
            )}
          </div>

          {/* 操作按钮组 */}
          {!readOnly && (
            <div className="flex items-center gap-2">
              {/* 摘要生成按钮 */}
              <button
                onClick={handleGenerateAbstract}
                disabled={isGeneratingAbstract || !content || content.length < MIN_CONTENT_LENGTH_FOR_ABSTRACT}
                className={`px-3 py-1.5 text-sm rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 flex items-center gap-1 ${
                  abstractCleared || chapter.chapter_abstract
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
                title={
                  isGeneratingAbstract
                    ? '正在生成摘要...'
                    : content?.length >= MIN_CONTENT_LENGTH_FOR_ABSTRACT
                      ? (abstractCleared || chapter.chapter_abstract ? '重新生成摘要' : '生成摘要')
                      : `内容不足${MIN_CONTENT_LENGTH_FOR_ABSTRACT}字，无法生成摘要`
                }
              >
                <Icons.fileText />
                {isGeneratingAbstract
                  ? '生成中...'
                  : (abstractCleared || chapter.chapter_abstract ? '重新生成摘要' : '生成摘要')
                }
              </button>

              {/* 知识图谱按钮 */}
              <button
                onClick={() => setShowGraphModal(true)}
                disabled={!content || content.length < MIN_CONTENT_LENGTH_FOR_ABSTRACT}
                className="px-3 py-1.5 text-sm bg-ink-800 hover:bg-ink-700 disabled:bg-ink-400 disabled:cursor-not-allowed text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 flex items-center gap-1"
                title={
                  content?.length >= MIN_CONTENT_LENGTH_FOR_ABSTRACT
                    ? '查看本章知识图谱'
                    : `内容不足${MIN_CONTENT_LENGTH_FOR_ABSTRACT}字，无法查看图谱`
                }
              >
                <Icons.knowledgeGraph />
                知识图谱
              </button>

              {/* 保存按钮 */}
              <button
                onClick={handleManualSave}
                disabled={isSaving}
                className="px-4 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 flex items-center gap-1 font-medium"
              >
                <Icons.save />
                保存
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 错误提示 */}
      {errorMessage && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-600 text-sm flex items-center justify-between">
          <span>{errorMessage}</span>
          <button
            onClick={() => setErrorMessage(null)}
            className="p-1 hover:bg-red-100 rounded"
          >
            <Icons.close />
          </button>
        </div>
      )}

      {/* 修改提示 */}
      {updateNotice && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm flex items-center justify-between animate-pulse">
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            内容已更新（位置: {updateNotice.startOffset}）
          </span>
          <button
            onClick={() => setUpdateNotice(null)}
            className="p-1 hover:bg-amber-100 rounded"
          >
            <Icons.close />
          </button>
        </div>
      )}

      {/* 编辑器主体 */}
      <div className="flex-1 flex overflow-hidden">
        {showHistory ? (
          /* 编辑历史模式 - 全屏对比视图 */
          <DiffViewer
            workspaceId={chapter.workspace_id}
            chapterId={chapter.id}
            currentContent={content}
            onClose={handleShowHistory}
          />
        ) : (
          /* 正常编辑模式 */
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* 摘要显示区域 */}
            {chapter.chapter_abstract && !abstractCleared && (
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Icons.fileText />
                  章节摘要
                </div>
                <p className="text-sm text-gray-600 italic leading-relaxed opacity-80">
                  {chapter.chapter_abstract}
                </p>
                {chapter.abstract_updated_at && (
                  <div className="text-xs text-gray-400 mt-1">
                    生成于 {new Date(chapter.abstract_updated_at).toLocaleString('zh-CN')}
                  </div>
                )}
              </div>
            )}

            {/* 摘要已清除提示 */}
            {abstractCleared && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm flex items-center justify-between">
                <span>摘要已因内容修改而清除，可重新生成</span>
                <button
                  onClick={handleGenerateAbstract}
                  disabled={isGeneratingAbstract}
                  className="text-amber-600 hover:text-amber-800 underline disabled:opacity-50"
                >
                  重新生成
                </button>
              </div>
            )}

            {/* 章节锁定提示 */}
            {isContentLocked && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>
                  {chapter.is_locked
                    ? '此章节已被锁定，正文不可编辑。章节标题仍可编辑。'
                    : '此章节为公共章节，正文已锁定不可编辑。章节标题仍可编辑。'}
                </span>
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              onSelect={handleSelectionChange}
              onKeyUp={handleSelectionChange}
              onMouseUp={handleMouseUp}
              readOnly={isEditorReadOnly}
              disabled={isEditorReadOnly}
              className={`chapter-editor-textarea flex-1 w-full p-4 resize-none focus:outline-none focus:ring-2 focus:ring-gray-400 text-gray-900 overflow-auto transition-all duration-500 ${
                showHighlight
                  ? 'bg-amber-50 border-2 border-amber-400 rounded-xl shadow-lg'
                  : isContentLocked
                    ? 'bg-gray-100 border-none'
                    : 'bg-white border border-gray-200 rounded-xl'
              }`}
              style={{
                fontSize: `${fontSize}px`,
                lineHeight: `${lineHeight}`,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}
              placeholder={isContentLocked ? (chapter.is_locked ? "章节内容已锁定" : "公共章节内容已锁定") : "开始撰写章节内容..."}
            />
          </div>
        )}
      </div>

      {/* 知识图谱模态框 */}
      {showGraphModal && (
        <KnowledgeGraphModal
          isOpen={showGraphModal}
          onClose={() => setShowGraphModal(false)}
          workspaceId={chapter.workspace_id}
          chapterNum={chapter.num}
          chapterId={chapter.id}
        />
      )}
    </div>
  );
};

export default ChapterEditor;